import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ArrowUpIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import { Box, Paper, Stack, Typography } from "@mui/material";
import type { PropsWithChildren, ReactNode } from "react";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";

interface Props {
  title: string;
  subtitle?: string;
  expanded?: boolean;
  onToggleExpanded?: () => void;
  flush?: boolean;
  centerContent?: ReactNode;
}

const PanelSectionHeader = ({
  title,
  subtitle,
  expanded,
  onToggleExpanded,
  flush,
  centerContent,
  children,
}: PropsWithChildren<Props>) => {
  const collapsible = expanded !== undefined && onToggleExpanded !== undefined;

  const handleClick = () => {
    if (collapsible) {
      onToggleExpanded();
    }
  };

  return (
    <Paper
      sx={{
        position: "sticky",
        top: 0,
        px: 2.5,
        border: 1,
        borderTop: flush ? 0 : 1,
        // Flush sections are separated by the parent's dividers, so a collapsed one must not add a second line.
        borderBottom: flush && expanded === false ? 0 : 1,
        borderColor: "divider",
        borderRadius: flush ? 0 : "4px 4px 0 0",
        borderLeft: 0,
        borderRight: 0,
        zIndex: 100,
      }}
      elevation={0}
    >
      <Box
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "4rem",
          cursor: collapsible ? "pointer" : undefined,
        }}
      >
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography variant="h6">{title}</Typography>
          {subtitle && (
            <TypographyTooltipEllipsis
              text={subtitle}
              observeContainerResize
              typographyProps={{ variant: "caption", color: "textSecondary" }}
            />
          )}
        </Stack>
        {centerContent && (
          <Stack
            direction="row"
            spacing={1}
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              alignItems: "center",
              cursor: "default",
            }}
          >
            {centerContent}
          </Stack>
        )}
        <Stack spacing={1} direction="row" sx={{ alignItems: "center" }}>
          {children}
          {collapsible && expanded && <ArrowUpIcon />}
          {collapsible && !expanded && <ArrowDownIcon />}
        </Stack>
      </Box>
    </Paper>
  );
};

export default PanelSectionHeader;
